import { useState, useEffect, useCallback } from "react";
import * as global from '../data/global';
import { MuiMaterials } from "../@index";
import GrainLevelIcon from '@mui/icons-material/Battery50';
import * as THREE from 'three';
import Delaunator from 'delaunator';
import { CSG } from 'three-csg-ts';
import { createApiEndpoint } from '../index.js';

export const GrainLevelGemini = () => {
    const [loading, setLoading] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    const handleGrainLevelClick = (e) => {
        setLoading(true);

        let target = e.currentTarget;
        if (!global.gw.selectedSite.showGrainLevels) {
            global.gw.selectedSite.showGrainLevels = true;
        } 
        else {
            global.gw.selectedSite.showGrainLevels = false;
        }

        setTimeout(() => {
            if (global.gw.selectedSite) {
                if (global.gw.selectedSite.showGrainLevels) {
                    let today = new Date();

                    let selectedDate = window.document.getElementsByClassName("dateLabel")[0];
                    if (selectedDate) {
                        let month = selectedDate.innerText.substring(0, 2);
                        let day = selectedDate.innerText.substring(3, 5);
                        let year = "20" + selectedDate.innerText.substring(6, 8);
                        //alert(`${year}-${month}-${day}T00:00:00.000Z`);
                        today = new Date(`${year}-${month}-${day}T00:00:00.000`);
                        //alert(today);
                    }

                    let dateFrom = today;
                    let dateTo = today;

                    let grainLevelPositions = [];
                    //if ((selectedDate && global.gw.selectedSite.grainLevelsDateFrom !== selectedDate.textContent) || global.gw.selectedSite.grainLevels.length === 0) {
                    global.gw.selectedSite.grainLevels = [];
                    const ctask = () => {
                        return createApiEndpoint().fetchGetSiteGrainLevels("compressed", null, null, null, null, null, null, dateFrom, dateTo, 0, 24, true, 5);
                    };
                    ctask().then((response) => {
                        if (response && response.data && response.data.fetchGetSiteGrainLevels) {
                            global.gw.selectedSite.grainLevels = response.data.fetchGetSiteGrainLevels;
                            response.data.fetchGetSiteGrainLevels.map(s => {
                                if (s && s.temperature !== "-E-") {
                                    if (global.gw.selectedSite.selectedBinId === 0 || (global.gw.selectedSite.selectedBinId > 0 && s.binId === global.gw.selectedSite.selectedBinId)) {
                                        grainLevelPositions.push(global.toggleCableLevelRef(s.binId, s.cableId, s.sensorId, s.position));
                                    }
                                }
                            });
                            renderGrainLevels(grainLevelPositions);
                            global.gw.selectedSite.grainLevelsDateFrom = selectedDate.textContent;
                            const isLight = global.gw.theme === 'light';
                            target.style.color = isLight ? "#333333" : "#f4cc8e";
                        }
                        setLoading(false);
                    }).catch((error) => {
                        const isLight = global.gw.theme === 'light';
                        target.style.color = isLight ? "#33333361" : "#f4cc8e61";
                        global.gw.selectedSite.showGrainLevels = false
                        setLoading(false);
                    });
                }
                else {
                    global.gw.selectedSite.showGrainLevels = false;
                    global.toggleCableLevelRef();
                    renderGrainLevels();
                    const isLight = global.gw.theme === 'light';
                    target.style.color = isLight ? "#33333361" : "#f4cc8e61";
                    setLoading(false);
                }
            }
        }, 10);
    };

    const renderGrainLevels = (grainLevelPositions) => {
        if (grainLevelPositions &&
            global.gw.selectedSite.binsRef.length > 0 &&
            grainLevelPositions.length > 0) {
            global.gw.selectedSite.binsRef.map(b => {
                if (b.layoutBin.binId) {
                    renderBinGrainLevel(grainLevelPositions, b);
                }
            });
        }
        else {
            global.gw.selectedSite.binsRef.map(b => {
                for (let i = 0; i < b.children.length; i++) {
                    let child = b.children[i];
                    if (b.children[i].isMesh) {
                        b.remove(child);
                        i--;
                    }
                }

                if (!b.parent ||
                    !b.parent.parent ||
                    !b.parent.parent.parent) {
                    return;
                }

                for (let i = 0; i < b.parent.parent.parent.children.length; i++) {
                    let child = b.parent.parent.parent.children[i];
                    if (child.isGrainLight) {
                        b.parent.parent.parent.remove(child);
                        i--;
                    }
                    //else if (child.isParticle) {
                    //    b.parent.parent.parent.remove(child);
                    //    i--;
                    //}
                }
            });
        }
    };

    const renderBinGrainLevel = async (grainLevelPositions, binRef) => {
        const boundingBox = new THREE.Box3().setFromObject(binRef);
        const binCenter = new THREE.Vector3();
        const binHeight = boundingBox.max.y;
        boundingBox.getCenter(binCenter);

        if (!binRef ||
            !binRef.position ||
            !binRef.parent ||
            !binRef.parent.position) {
            return;
        }

        let siteWorld = new THREE.Vector3(binRef.parent.position.x, binRef.parent.position.y, binRef.parent.position.z);
        let binWorld = new THREE.Vector3(binRef.position.x, binRef.position.y, binRef.position.z);

        let lengthFromCenters = [];
        let layerIndex = -1;
        let currentLayer = null;

        let sumY = 0;
        let countY = 0;

        grainLevelPositions.filter(p => p.binId === binRef.layoutBin.binId).map((g, i) => {
            if (g.position.y >= 1) {
                sumY += g.position.y;
                countY++;
            }
        });
        let averageY = 0;
        if (countY > 0) {
            averageY = sumY / countY;
        }

        let binGrainLevelPositions = grainLevelPositions.filter(p => p.binId === binRef.layoutBin.binId).map((g, i) => {
            if (g.position.y < 1) {
                g.position.y = averageY;
            }

            let lengthFromCenter = Math.round((global.getPointToPointLength(0, 0, g.position.x, g.position.z) * 10)) / 10;
            let angle = Math.round((global.getAngleRadiansBetweenPoints(0, 0, g.position.x, g.position.z) * 180 / Math.PI * 10)) / 10;
            angle = ((angle % 360) + 360) % 360;
            if (lengthFromCenter !== currentLayer) {
                currentLayer = lengthFromCenter;
                layerIndex++;
            }

            let cableName = null;
            try {
                cableName = global.gw.selectedSite.cables.filter(x => x.id === g.cableId)[0].name
            } catch {

            }

            let position = g.position.clone().add(binWorld.clone().add(siteWorld.clone()));

            lengthFromCenters.push({
                cableId: g.cableId,
                cableName: cableName,
                lengthFromCenter: lengthFromCenter,
                angle: angle,
                layerIndex: layerIndex,
                layerPoint: position,
            });

            return position;
        });

        //  Get outer edge points
        //  Figure out layers in 2d (x, z)
        //  Center point will be (0, 0)
        let outerLayerPoints = lengthFromCenters.filter(x => x.layerIndex === layerIndex);
        let beforeOuterLayerPoints = lengthFromCenters.filter(x => x.layerIndex === layerIndex - 1);
        let withinAngle = 0;
        if (beforeOuterLayerPoints.length === 0) {
            //  One Layer
        }
        else {
            withinAngle = 360 / beforeOuterLayerPoints.length;
        }

        let radius = binRef.width / 2.0;
        let isThreePoint = false;

        for (let i = 0; i < outerLayerPoints.length; i++) {
            let outerLayerPoint = outerLayerPoints[i];
            let nearstLayerPoints = beforeOuterLayerPoints.filter(x =>
                x.angle <= outerLayerPoint.angle + withinAngle &&
                x.angle >= outerLayerPoint.angle - withinAngle);

            if (nearstLayerPoints.length === 0 && outerLayerPoints.length === 1 && outerLayerPoint.layerPoint.y > 5) {

            }
            else if (nearstLayerPoints.length === 0 && outerLayerPoints.length === 3) {
                isThreePoint = true;
                processPoints(outerLayerPoints.map(p => { return p.layerPoint }), radius, binGrainLevelPositions);
            }
            else if (nearstLayerPoints.length + outerLayerPoints.length === 3) {
                isThreePoint = true;
                processPoints(outerLayerPoints.concat(nearstLayerPoints).map(p => { return p.layerPoint }), binRef.width + 5, binGrainLevelPositions);
            }
            else {
                //  calculate end points
                for (var n = 0; n < nearstLayerPoints.length; n++) {
                    let point = nearstLayerPoints[n];


                    let lineStart = new THREE.Vector3(point.layerPoint.x, point.layerPoint.y, point.layerPoint.z);
                    //if (lineStart) {
                    //    let pointGeometry = new THREE.BoxGeometry(2, 2, 2);
                    //    let mesh = new THREE.Mesh(pointGeometry, material);
                    //    mesh.isGrainLight = true;
                    //    mesh.position.set(lineStart.x, lineStart.y, lineStart.z);
                    //    b.parent.parent.parent.add(mesh);
                    //}

                    let lineEnd = new THREE.Vector3(outerLayerPoint.layerPoint.x, outerLayerPoint.layerPoint.y, outerLayerPoint.layerPoint.z);
                    //if (lineEnd) {
                    //    let pointGeometry = new THREE.BoxGeometry(2, 2, 2);
                    //    let mesh = new THREE.Mesh(pointGeometry, material);
                    //    mesh.isGrainLight = true;
                    //    mesh.position.set(lineEnd.x, lineEnd.y, lineEnd.z);
                    //    b.parent.parent.parent.add(mesh);
                    //}

                    let lineExtended = global.extendLine(lineStart, lineEnd, binRef.width + 5);


                    binGrainLevelPositions.push(new THREE.Vector3(lineExtended.x, lineExtended.y, lineExtended.z));
                }
            }
        }

        let geometry = null;
        let mesh = null;

        const clientDeviceInfo = global.getClientDeviceInfo();
        let segments = 100;
        if (!clientDeviceInfo.isDesktop) {
            segments = 40;
        }

        if (outerLayerPoints.length === 0) {
            // do nothing
        }
        else if (outerLayerPoints.length === 1) {
            if (outerLayerPoints[0].layerPoint.y > 2) {
                geometry = new THREE.CylinderGeometry(radius, radius, outerLayerPoints[0].layerPoint.y + 1.5, segments);
                mesh = new THREE.Mesh(geometry, global.grainLevelMaterial);
                //mesh.setRotationFromEuler(new THREE.Euler(Math.PI / 2, 0, 0));
                if (beforeOuterLayerPoints.length === 0) {
                    mesh.position.set(binCenter.x, outerLayerPoints[0].layerPoint.y / 2 - 0.75, binCenter.z);
                }
                else {
                    mesh.position.set(outerLayerPoints[0].layerPoint.x, outerLayerPoints[0].layerPoint.y / 2 - 0.75, outerLayerPoints[0].layerPoint.z);
                }
                binRef.children.push(mesh);
            }
        }
        else {
            const cylinderGeometry = new THREE.CylinderGeometry(radius, radius, binHeight, segments, segments);
            let shapeMesh = new THREE.Mesh(cylinderGeometry, global.grainLevelMaterial);
            shapeMesh.position.set(binCenter.x, binHeight / 2 - 1.5, binCenter.z);

            geometry = new THREE.BufferGeometry().setFromPoints(binGrainLevelPositions);
            const delaunay = Delaunator.from(binGrainLevelPositions.map(p => [p.x, p.z]));
            const indices = [];
            for (let i = 0; i < delaunay.triangles.length; i += 3) {
                indices.push(delaunay.triangles[i], delaunay.triangles[i + 1], delaunay.triangles[i + 2]);
            }
            geometry.setIndex(indices);
            geometry.computeVertexNormals();
            mesh = new THREE.Mesh(geometry, global.grainLevelMaterial);

            mesh.updateMatrix();
            shapeMesh.updateMatrix();

            let bspB = CSG.fromMesh(mesh)
            let bspA = CSG.fromMesh(shapeMesh)
            const intRes = bspA.intersect(bspB);
            let meshResult = CSG.toMesh(intRes, mesh.matrix, global.grainLevelMaterial)
            binRef.children.push(meshResult);

            //let subRes = bspA.subtract(bspB);
            //const shapeMaterial = new THREE.MeshStandardMaterial({
            //    color: "rgb(255, 255, 255)",
            //    emissive: "rgb(0, 0, 0)",
            //    emissiveIntensity: 32,
            //    intensity: 0.9,
            //    toneMapped: false,
            //    envMapIntensity: 1,
            //    transparent: true,
            //    opacity: 0.0015,
            //    depthTest: true,
            //    depthWrite: true,
            //    side: THREE.DoubleSide,
            //    roughness: 0.8,
            //    metalness: 0.4,
            //    //fog: true,
            //    //wireframe: false,
            //    //normalMap: normalMapTexture,
            //});
            //meshResult = CSG.toMesh(subRes, mesh.matrix, shapeMaterial);
            //b.children.push(meshResult);
        }

        if (mesh) {
            const light = new THREE.DirectionalLight(0xebd37c);
            light.intensity = 0.1;
            light.castShadow = true;
            light.isGrainLight = true;
            light.position.set(binCenter.x, 2, binCenter.z);// + b.width / 2.0);
            light.target = mesh;
            binRef.parent.parent.parent.add(light);

            // let pointGeometry = new THREE.BoxGeometry(5, 5, 5);
            // mesh = new THREE.Mesh(pointGeometry, global.grainLevelMaterial);
            // mesh.isGrainLight = true;
            // mesh.position.set(light.position.x, light.position.y, light.position.z);
            // binRef.parent.parent.parent.add(mesh);
        }
    };

    const processPoints = (points, radius, binGrainLevelPositions) => {
        if (!points || points.length < 1) {
            return;
        }

        for (let p = 0; p < points.length; p++) {
            let thisPoint = points[p];
            points[p] = new THREE.Vector3(thisPoint.x, thisPoint.y, thisPoint.z);
        }

        for (let p = 0; p < points.length; p++) {
            let thisPoint = points[p];
            for (let op = 0; op < points.length; op++) {
                if (op === p) {
                    continue;
                }

                let otherPoint = points[op];
                let extendedPointAB = global.extendLine(thisPoint, otherPoint, radius);
                let extendedPointBA = global.extendLine(otherPoint, thisPoint, radius);

                binGrainLevelPositions.push(new THREE.Vector3(extendedPointAB.x, extendedPointAB.y, extendedPointAB.z));
                binGrainLevelPositions.push(new THREE.Vector3(extendedPointBA.x, extendedPointBA.y, extendedPointBA.z));
            }
        }
    };

    return (
        <MuiMaterials.Box style={{ margin: "0px" }}>
            <MuiMaterials.Tooltip title="Toggles Grain Levels" arrow>
                <MuiMaterials.Button
                type="button"
                //loading={loading}
                disabled={loading}
                size="medium"
                key={`grain-level-button-${global.gw.theme}-${forceUpdate}`}
                sx={{
                    color: getThemeColor('#33333361', '#f4cc8e61'),
                    margin: "auto",
                    maxWidth: "30px",
                    maxHeight: "40px",
                    minWidth: "30px",
                    minHeight: "40px",
                    '&:hover': {
                        backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                    },
                    '& .MuiSvgIcon-root': {
                        color: getThemeColor('#33333361', '#f4cc8e61'),
                    },
                    '& .gli': {
                        color: getThemeColor('#33333361', '#f4cc8e61'),
                    },
                }}
                onClick={handleGrainLevelClick}
            >
                {loading ? 
                    <MuiMaterials.RotatingLines
                        strokeColor={getThemeColor('#333333', '#f4cc8e')}
                        strokeWidth="3"
                        animationDuration="0.72"
                        width="48"
                        visible={true}
                    />
                    :
                    <GrainLevelIcon className="gli" />
                }
                </MuiMaterials.Button>
            </MuiMaterials.Tooltip>
        </MuiMaterials.Box>
    );
}

export default GrainLevelGemini;